import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDataProvider } from '@/providers/DataProviderContext'
import { Book as BookType, Chapter, ExportSettings } from '@/core/models'
import {
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Input,
  WordCount,
  AutoSaveIndicator,
  Logo,
  RichTextEditor,
  ExportSettingsModal,
} from '@/components'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Download,
  ChevronRight,
  ChevronDown,
  GripVertical,
  FileText,
  Menu,
  X,
} from 'lucide-react'

export function BookEditor() {
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const dataProvider = useDataProvider()

  const [book, setBook] = useState<BookType | null>(null)
  const [chapters, setChapters] = useState<Chapter[]>([])
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'idle'>('saved')
  const [lastSaved, setLastSaved] = useState<Date>(new Date())

  // Modal states
  const [isNewChapterModalOpen, setIsNewChapterModalOpen] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [newChapterTitle, setNewChapterTitle] = useState('')
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Load book and chapters
  useEffect(() => {
    if (!bookId) return
    loadBookData()
  }, [bookId])

  // Auto-save effect
  useEffect(() => {
    if (!selectedChapterId || content === '') return

    const timeoutId = setTimeout(() => {
      saveContent()
    }, 2000) // Auto-save after 2 seconds of no typing

    return () => clearTimeout(timeoutId)
  }, [content, selectedChapterId])

  const loadBookData = async () => {
    if (!bookId) return

    setLoading(true)
    try {
      const fetchedBook = await dataProvider.getBook(bookId)
      setBook(fetchedBook)

      // Load chapters
      const chapterPromises = (fetchedBook.chapterIds || []).map(id =>
        dataProvider.getChapter(id),
      )
      const fetchedChapters = await Promise.all(chapterPromises)
      setChapters(fetchedChapters)

      // Select first chapter if available
      if (fetchedChapters.length > 0) {
        setSelectedChapterId(fetchedChapters[0].id)
        setContent(fetchedChapters[0].content)
      }
    } catch (error) {
      console.error('Failed to load book:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveContent = async () => {
    if (!selectedChapterId) return

    setSaveStatus('saving')
    try {
      await dataProvider.updateChapter(selectedChapterId, {
        content,
      })

      // Update local chapter list
      setChapters(prevChapters =>
        prevChapters.map(ch =>
          ch.id === selectedChapterId ? { ...ch, content } : ch,
        ),
      )

      setSaveStatus('saved')
      setLastSaved(new Date())
    } catch (error) {
      console.error('Failed to save content:', error)
      setSaveStatus('error')
    }
  }

  const handleSelectChapter = async (chapterId: string) => {
    // Save current chapter before switching
    if (selectedChapterId) {
      await saveContent()
    }

    setSelectedChapterId(chapterId)
    const chapter = chapters.find(c => c.id === chapterId)
    if (chapter) {
      setContent(chapter.content)
    } else {
      setContent('')
    }

    // Close sidebar on mobile after selecting chapter
    setIsSidebarOpen(false)
  }

  const handleCreateChapter = async () => {
    if (!bookId || !newChapterTitle.trim()) return

    try {
      const newChapter = await dataProvider.createChapter({
        bookId,
        title: newChapterTitle.trim(),
        order: chapters.length,
      })

      // Reload book data
      await loadBookData()

      // Reset modal
      setNewChapterTitle('')
      setIsNewChapterModalOpen(false)

      // Select the new chapter
      setSelectedChapterId(newChapter.id)
    } catch (error) {
      console.error('Failed to create chapter:', error)
    }
  }

  const handleDeleteChapter = async (chapterId: string) => {
    if (!bookId) return
    if (!confirm('Are you sure you want to delete this chapter? This action cannot be undone.')) {
      return
    }

    try {
      await dataProvider.deleteChapter(chapterId)

      // If deleted chapter was selected, select another
      if (selectedChapterId === chapterId) {
        setSelectedChapterId(null)
        setContent('')
      }

      // Reload book data
      await loadBookData()
    } catch (error) {
      console.error('Failed to delete chapter:', error)
    }
  }

  const handleExport = async (
    format: 'pdf' | 'epub' | 'markdown' | 'html',
    settings: ExportSettings,
  ) => {
    if (!bookId) return

    try {
      const exportResult = await dataProvider.exportBook(bookId, {
        format,
        includeMetadata: true,
        includeTOC: settings.includeTableOfContents,
        settings,
      })

      if (!exportResult.success || !exportResult.blob) {
        alert(exportResult.error || 'Export failed')
        return
      }

      // Create download link
      const url = URL.createObjectURL(exportResult.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = exportResult.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setIsExportModalOpen(false)
    } catch (error) {
      console.error('Failed to export book:', error)
      alert('Export failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="xl" />
      </div>
    )
  }

  if (!book) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Book not found</h2>
          <Button onClick={() => navigate('/')}>Go to Dashboard</Button>
        </div>
      </div>
    )
  }

  const selectedChapter = chapters.find(c => c.id === selectedChapterId)

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          {/* Left Section */}
          <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Logo (desktop) */}
            <div className="hidden lg:block">
              <Logo size="sm" showText={false} />
            </div>

            {/* Divider (desktop) */}
            <div className="hidden lg:block h-8 w-px bg-gray-300" />

            {/* Back button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="hidden sm:flex"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {/* Book title */}
            <div className="flex-1 min-w-0">
              <h1 className="text-sm md:text-base lg:text-lg font-bold text-gray-900 truncate">
                {book.title}
              </h1>
              <p className="text-xs text-gray-600 truncate hidden sm:block">by {book.author}</p>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            <AutoSaveIndicator status={saveStatus} lastSaved={lastSaved} className="hidden lg:flex" />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsExportModalOpen(true)}
            >
              <Download className="h-4 w-4" />
              <span className="hidden md:inline ml-2">Export</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Chapter Sidebar */}
        <div
          className={`
            fixed md:relative inset-y-0 left-0 z-50 md:z-0
            w-72 md:w-72 bg-white border-r border-gray-200 flex flex-col
            transform transition-transform duration-200 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        >
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-gray-900">Chapters</h2>
                <Badge variant="default">{chapters.length}</Badge>
              </div>
              {/* Close button for mobile */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarOpen(false)}
                className="md:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => setIsNewChapterModalOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Chapter
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {chapters.length === 0 ? (
              <div className="p-6 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-4">
                  No chapters yet. Create your first chapter to start writing.
                </p>
              </div>
            ) : (
              <div className="py-2">
                {chapters.map((chapter, index) => (
                  <div
                    key={chapter.id}
                    className={`group flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                      selectedChapterId === chapter.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => handleSelectChapter(chapter.id)}
                  >
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Ch. {index + 1}</span>
                        {selectedChapterId === chapter.id ? (
                          <ChevronDown className="h-4 w-4 text-blue-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <p className="font-medium text-gray-900 truncate">{chapter.title}</p>
                      <p className="text-xs text-gray-500">
                        {chapter.wordCount || 0} words
                      </p>
                    </div>
                    <button
                      className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                      onClick={e => {
                        e.stopPropagation()
                        handleDeleteChapter(chapter.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {selectedChapter ? (
            <>
              {/* Rich Text Editor */}
              <div className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                    {selectedChapter.title}
                  </h2>
                  <RichTextEditor
                    value={content}
                    onChange={setContent}
                    placeholder="Start writing your chapter here..."
                    minHeight="400px"
                  />
                </div>
              </div>

              {/* Editor Footer */}
              <div className="border-t border-gray-200 px-4 md:px-8 py-3 md:py-4">
                <WordCount text={content.replace(/<[^>]*>/g, '')} />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center max-w-md">
                <FileText className="h-12 w-12 md:h-16 md:w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                  No chapter selected
                </h3>
                <p className="text-sm md:text-base text-gray-600 mb-6">
                  {chapters.length === 0
                    ? 'Create your first chapter to start writing'
                    : 'Select a chapter from the sidebar or create a new one'}
                </p>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsNewChapterModalOpen(true)
                    setIsSidebarOpen(false)
                  }}
                  size="md"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {chapters.length === 0 ? 'Create First Chapter' : 'New Chapter'}
                </Button>
                {chapters.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => setIsSidebarOpen(true)}
                    className="mt-3 md:hidden"
                    fullWidth
                  >
                    <Menu className="h-4 w-4 mr-2" />
                    View Chapters
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chapter Modal */}
      <Modal
        open={isNewChapterModalOpen}
        onClose={() => setIsNewChapterModalOpen(false)}
      >
        <ModalHeader>
          <ModalTitle>Create New Chapter</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Input
            label="Chapter Title"
            placeholder="e.g., Chapter 1: The Beginning"
            value={newChapterTitle}
            onChange={e => setNewChapterTitle(e.target.value)}
            required
            fullWidth
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsNewChapterModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateChapter}
            disabled={!newChapterTitle.trim()}
          >
            Create Chapter
          </Button>
        </ModalFooter>
      </Modal>

      {/* Export Settings Modal */}
      <ExportSettingsModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
      />
    </div>
  )
}
